import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Modal, Spinner } from "react-bootstrap";
import { X, MapPin, User, Hash, Navigation, Clock } from "lucide-react";
import { toast } from "react-toastify";
import { Base_URL } from "../url";
import Attendance from "./Attendance";

const AttendanceModal = ({ show, onHide, user }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [geoFenceNotFound, setGeoFenceNotFound] = useState(false);

 useEffect(() => {
  if (show) {
    // 🔄 FULL RESET on open
    setLoading(false);
    setStatus(null);
    setAttendanceStatus(null);
    setLocation({ latitude: null, longitude: null, address: null, cityOrigin: null });
    setIsAddressResolved(false);
    setGeoFenceData(null);
    setGeoDistance(null);
    setIsInside(null);
    setGeoFenceNotFound(false);
    setGeoLocationError(false);
    isFetchingLocation.current = false; // ✅ ADD THIS LINE
  }
}, [show]);

  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    address: null,
    cityOrigin: null,
  });
  const [geoFenceData, setGeoFenceData] = useState(null);
  const [geoDistance, setGeoDistance] = useState(null);
  const [isInside, setIsInside] = useState(null);
  const [geoLocationError, setGeoLocationError] = useState(false);
  const [isAddressResolved, setIsAddressResolved] = useState(false);

  const geoMode = geoFenceData?.userStation?.geoFencingCheckForUser; // "E" | "W"
  const isGeoEnabled = geoFenceData?.userStation?.geoFencingEnabled;
  const isOutside = isInside === false;

  // ENFORCED MODE → block
  const disableGeoActions = isGeoEnabled && geoMode === "E" && isOutside;

  // WARNING MODE → allow + warn
  const showGeoWarning = isGeoEnabled && geoMode === "W" && isOutside;

  // ENFORCED MODE

  const convertToMeters = (value, unit) => {
    if (!value) return 0;

    switch (unit?.toLowerCase()) {
      case "m":
      case "meter":
      case "meters":
        return value;
      case "km":
        return value * 1000;
      case "cm":
        return value / 100;
      default:
        return value; // fallback → assume meters
    }
  };
  function isInsideGeofence(
    userLat,
    userLon,
    fenceLat,
    fenceLon,
    radiusMeters,
  ) {
    const R = 6371000; // meters
    const toRad = (deg) => (deg * Math.PI) / 180;

    const dLat = toRad(userLat - fenceLat);
    const dLon = toRad(userLon - fenceLon);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(fenceLat)) *
        Math.cos(toRad(userLat)) *
        Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return {
      isInside: distance <= radiusMeters,
      distance: distance, // meters
    };
  }
  const getGeoFencingData = async () => {
    try {
      const res = await axios.get(
        `${Base_URL}/GeoFencing/GetStationCordinates/${user?.userId}`,
      );

      setGeoFenceData(res.data);
      setGeoFenceNotFound(false);
    } catch (err) {
      if (err.response?.status === 404) {
        // 👇 IMPORTANT: explicitly handle 404
        setGeoFenceNotFound(true);
        setGeoFenceData(null);
      } else {
        console.error("Error fetching geo fencing data", err);
        toast.error("Unable to fetch geo fencing details");
      }
    }
  };
  const isFetchingLocation = useRef(false);
 const getLocation = () => {
  if (isFetchingLocation.current) return; // 🛑 Prevent double-fetch
  isFetchingLocation.current = true;

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        try {
          const geoRes = await axios.get(
            `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lon}&apiKey=ac8a8a05e1194e3a8f0c5a3872610ab1`
          );

          const props = geoRes.data?.features?.[0]?.properties;

          if (!props) throw new Error("No geocoding result");

          const cityOrigin = props.city || props.county || props.state || "Unknown";
          const city = props.formatted || cityOrigin;

          setLocation({ latitude: lat, longitude: lon, address: city, cityOrigin });
          setIsAddressResolved(true);
        } catch (err) {
          console.error("Error fetching city:", err);
          toast.error("Unable to fetch city name from coordinates.");
          setLocation({ latitude: lat, longitude: lon, address: "Unknown", cityOrigin: "Unknown" });
          setIsAddressResolved(true);
        } finally {
          isFetchingLocation.current = false; // ✅ Always release
        }
      },
      (error) => {
        isFetchingLocation.current = false;
        console.error("Error getting location:", error);
        if (error.code === 1) {
          toast.error("Location access denied. Please enable location permissions and try again.");
        } else {
          toast.error("Unable to get location. Please enable location services.");
        }
        setGeoLocationError(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  } else {
    isFetchingLocation.current = false;
    toast.error("Geolocation is not supported by this browser.");
  }
};

  useEffect(() => {
    if (show) {
      getLocation();
      getAttendanceStatus();
      getGeoFencingData();
    }
  }, [show]);

  useEffect(() => {
    if (
      !geoLocationError &&
      !geoFenceNotFound &&
      location.latitude &&
      location.longitude &&
      geoFenceData?.stationCordinates &&
      geoFenceData?.userStation?.geoFencingEnabled
    ) {
      const fenceLat = parseFloat(
        geoFenceData.stationCordinates.stationLatitude,
      );
      const fenceLon = parseFloat(
        geoFenceData.stationCordinates.stationLongitude,
      );

      const radiusMeters = convertToMeters(
        geoFenceData.userStation.geoFencingDistance,
        geoFenceData.userStation.geoFencingDistanceUnit,
      );

      const result = isInsideGeofence(
        location.latitude,
        location.longitude,
        fenceLat,
        fenceLon,
        radiusMeters,
      );

      setIsInside(result.isInside);
      setGeoDistance(result.distance);
    } else {
      // 👇 reset silently
      setGeoDistance(null);
      setIsInside(null);
    }
  }, [location, geoFenceData, geoLocationError, geoFenceNotFound]);

  const handleClock = async (actionType) => {
         if (!location.address || !location.cityOrigin) {
  toast.error("Location details still loading. Please wait.");
  return;
}
    try {
      setLoading(true);
      const now = new Date();
 

      const payload = {
        userId: user?.userId,
        Date: now.toISOString().split("T")[0],
        Time: now.toLocaleTimeString("en-GB", { hour12: false }),
        type: actionType,
        organisationId: user?.organisationId,
        latitude: String(location.latitude),
        longitude: String(location.longitude),
        address: location.address,
        city: location.cityOrigin,
        distanceFromStation: geoDistance,
        distanceFromStationUnit:
          geoFenceData?.userStation?.geoFencingDistanceUnit,
      };

      console.log("🧾 Payload:", payload);

      const endpoint =
        actionType === "clock_in"
          ? `${Base_URL}/Attendance/clockIn`
          : `${Base_URL}/Attendance/clockOut`;

      const res = await axios.post(endpoint, payload);

      if (res.status === 200 && actionType === "clock_in") {
        toast.success("Clocked In Successfully");
        setStatus("You have successfully clocked in!");
      } else if (res.status === 200 && actionType === "clock_out") {
        toast.success("Clocked Out Successfully");
        setStatus("You have successfully clocked out!");
      } else {
        toast.error(res.data);
      }

      await getAttendanceStatus();
    } catch (error) {
      console.error("Error while clocking:", error);
      setStatus("Error while clocking. Please try again.");
      toast.error("Error while clocking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStatus = async () => {
    try {
      const res = await axios.get(
        `${Base_URL}/Attendance/GetAttendanceStatus/id?id=${user?.userId}`,
      );
      setAttendanceStatus(res.data);
    } catch (error) {
      console.error("Error fetching attendance status:", error);
    }
  };
  console.log(attendanceStatus, "ajfnisaf");
const isLocationAvailable =
  location.latitude &&
  location.longitude &&
  location.address &&
  location.cityOrigin;

  const isGeoResolved =
    !geoFenceData || // no geofence configured (404 / disabled)
    geoFenceData?.userStation?.geoFencingEnabled === false ||
    isInside !== null; // geo check completed

  const isAttendanceResolved =
    attendanceStatus !== null && attendanceStatus !== undefined;

const isReadyForAction =
  isLocationAvailable &&
  isAddressResolved &&
  isGeoResolved &&
  isAttendanceResolved &&
  !loading;


  return (
    <>
      <style>{`
        /* Blur background effect */
        .attendance-modal .modal-backdrop {
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
          background-color: rgba(0, 0, 0, 0.2) !important;
        }

        .attendance-modal .modal-content {
          border-radius: 16px;
          border: none;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.02);
          overflow: hidden;
        }

        .attendance-modal .modal-dialog {
          max-width: 400px;
        }

        .attendance-modal-header {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%);
          color: white;
          padding: 15px;
          border: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .attendance-modal-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .attendance-close-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 8px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 0;
          color: white;
        }

        .attendance-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .attendance-close-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .attendance-modal-body {
          padding: 20px 24px;
        }

        .attendance-info-section {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .attendance-info-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 14px;
          font-size: 14px;
        }

        .attendance-info-item:last-child {
          margin-bottom: 0;
        }

        .attendance-info-icon {
          margin-right: 12px;
          color: #6366f1;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .attendance-info-content {
          flex: 1;
        }

        .attendance-info-label {
          font-weight: 600;
          color: #495057;
          display: block;
          margin-bottom: 2px;
        }

        .attendance-info-value {
          color: #6c757d;
          word-break: break-word;
        }

        .attendance-location-warning {
          background: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 8px;
          padding: 14px;
          margin-bottom: 20px;
          font-size: 14px;
          color: #856404;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .attendance-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 24px;
        }

        .attendance-btn {
          padding: 14px 24px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 16px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .attendance-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
        }

        .attendance-btn-in {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .attendance-btn-in:not(:disabled):hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
        }

        .attendance-btn-out {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .attendance-btn-out:not(:disabled):hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
        }

        .attendance-status-message {
          background: #d1fae5;
          border: 1px solid #10b981;
          border-radius: 8px;
          padding: 14px;
          margin-top: 16px;
          text-align: center;
          color: #065f46;
          font-weight: 500;
        }

        .attendance-spinner-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px 0;
        }

        @media (max-width: 640px) {
          .attendance-modal-header {
            padding: 20px;
          }

          .attendance-modal-title {
            font-size: 20px;
          }

          .attendance-modal-body {
            padding: 24px 20px;
          }

          .attendance-buttons {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <Modal
        show={show}
        onHide={loading ? undefined : onHide}
        centered
        backdrop={loading ? "static" : true}
        className="attendance-modal"
      >
        <div className="attendance-modal-header">
          <h4 className="attendance-modal-title">
            <MapPin size={24} />
            Post Attendance
          </h4>
          <button
            className="attendance-close-btn"
            onClick={onHide}
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <Modal.Body className="attendance-modal-body">
          {geoDistance !== null && (
            <div className="attendance-location-warning">
              <Navigation size={16} />
              <span>
                Distance from station: <b>{geoDistance.toFixed(2)} meters</b>
                <br />
                Status:{" "}
                <b style={{ color: isInside ? "green" : "red" }}>
                  {isInside ? "Inside Geofence" : "Outside Geofence"}
                </b>
              </span>
            </div>
          )}

          <Attendance
            onClock={handleClock}
            loading={loading}
            disableActions={disableGeoActions}
            showGeoWarning={showGeoWarning}
            isReady={isReadyForAction}
            distance={geoDistance}
            unit={geoFenceData?.userStation?.geoFencingDistanceUnit}
            attendanceStatus={attendanceStatus}
            location={location}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AttendanceModal;
